/**
 * How the deputy is working today: ROUTE (a planned order, by likelihood of service or by
 * location with a chosen start and finish) or FREELANCE (just ride; the list re-sorts nearest
 * first as they drive, unless "Don't rearrange by location" is ticked).
 * Remembered for the day on this device.
 */
import { useEffect, useState } from 'react';
import { addressKey, distanceFeet, planRoute } from '@civilos/domain';
import { api } from '../shared/api.ts';
import { ErrorBox, useAction } from '../shared/ui.tsx';

export interface Place {
  kind: 'here' | 'office' | 'address' | 'anywhere' | 'start';
  label: string;
  lat?: number;
  lng?: number;
}

export interface WorkMode {
  day: string;
  mode: 'route' | 'freelance';
  by?: 'likelihood' | 'location';
  start?: Place;
  finish?: Place;
  planned?: string[];
  plannedMiles?: number;
  plannedStops?: number;
  lock?: boolean;
  frozen?: string[];
}

export interface OrderItem {
  id: string;
  pin: { lat: number; lng: number } | null;
  address: any;
  est: { personalPct: number | null; substitutePct: number | null };
  open: boolean;
  /** A qualified attempt can be made right now. Only these go into a planned route. */
  eligibleNow: boolean;
}

const key = (userId: string) => `civilhound-workmode-${userId}`;

export function useWorkMode(userId: string | undefined, today: string): [WorkMode | null, (w: WorkMode | null) => void] {
  const [wm, setWm] = useState<WorkMode | null>(() => {
    if (!userId) return null;
    try {
      const v = JSON.parse(localStorage.getItem(key(userId)) ?? 'null') as WorkMode | null;
      return v && v.day === today ? v : null;
    } catch {
      return null;
    }
  });
  useEffect(() => {
    if (wm && wm.day !== today) setWm(null);
  }, [today]);
  const set = (w: WorkMode | null) => {
    setWm(w);
    try {
      if (!userId) return;
      if (w) localStorage.setItem(key(userId), JSON.stringify(w));
      else localStorage.removeItem(key(userId));
    } catch {
      /* private window: keep it in memory only */
    }
  };
  return [wm, set];
}

const MI = 5280;
const likelihood = (x: OrderItem) => (x.est.personalPct == null && x.est.substitutePct == null ? -1 : (x.est.personalPct ?? 0) + (x.est.substitutePct ?? 0));

/** Order of the open papers for the chosen mode (ids). Null when no mode is chosen. */
export function orderFor(items: OrderItem[], wm: WorkMode | null, pos: { lat: number; lng: number } | null): string[] | null {
  if (!wm) return null;
  // Freelance is "just ride": everything by distance, as before.
  if (wm.mode === 'freelance') return orderOpen(items.filter((x) => x.open), wm, pos);
  // A planned route: papers eligible right now first, in route order; the rest follow (by distance) and aren't stops.
  const ids = orderOpen(items.filter((x) => x.open && x.eligibleNow), wm, pos);
  const rest = items.filter((x) => x.open && !x.eligibleNow);
  const d = (x: OrderItem) => (x.pin && pos ? distanceFeet(pos, x.pin) : 1e12);
  return [...ids, ...rest.sort((a, b) => d(a) - d(b)).map((x) => x.id)];
}

function orderOpen(open: OrderItem[], wm: WorkMode, pos: { lat: number; lng: number } | null): string[] {
  const dist = (x: OrderItem) => (x.pin && pos ? distanceFeet(pos, x.pin) / MI : 1e9);
  const byDistance = (a: OrderItem, b: OrderItem) => dist(a) - dist(b);
  const byList = (list: string[] | undefined, fallback: (a: OrderItem, b: OrderItem) => number) => {
    const idx = new Map((list ?? []).map((id, i) => [id, i]));
    return [...open].sort((a, b) => (idx.get(a.id) ?? 1e9) - (idx.get(b.id) ?? 1e9) || fallback(a, b)).map((x) => x.id);
  };
  if (wm.mode === 'freelance') return wm.lock ? byList(wm.frozen, byDistance) : [...open].sort(byDistance).map((x) => x.id);
  if (wm.by === 'location') return byList(wm.planned, byDistance);
  return [...open].sort((a, b) => likelihood(b) - likelihood(a) || byDistance(a, b)).map((x) => x.id);
}

export function planOrder(items: OrderItem[], start: { lat: number; lng: number }, finish: { lat: number; lng: number } | null) {
  // Only papers that can be attempted (and count) right now. Ones that open up later get added when re-planned.
  const stops = items.filter((x) => x.open && x.eligibleNow && x.pin).map((x) => ({ id: x.id, lat: x.pin!.lat, lng: x.pin!.lng, addressKey: addressKey(x.address) }));
  const r = planRoute(stops, start, finish);
  return { planned: r.stops.flatMap((s) => s.paperIds), miles: r.totalMiles, stops: r.stops.length, skipped: items.filter((x) => x.open && !x.eligibleNow).length };
}

export function describeMode(wm: WorkMode): string {
  if (wm.mode === 'freelance') return wm.lock ? 'Freelance · order kept' : 'Freelance · nearest first';
  if (wm.by === 'likelihood') return 'Route · best chance of service first';
  const fin = wm.finish?.kind === 'anywhere' || !wm.finish ? 'end anywhere' : `finish: ${wm.finish.kind === 'start' ? 'back to start' : wm.finish.label}`;
  return `Route · by location · start: ${wm.start?.label ?? 'here'} · ${fin}${wm.plannedStops != null ? ` · ${wm.plannedStops} eligible stop${wm.plannedStops === 1 ? '' : 's'}` : ''}${wm.plannedMiles != null ? ` · ~${wm.plannedMiles} mi` : ''}`;
}

type Step = 'mode' | 'by' | 'where';

/** "How are you working today?" — Route (likelihood / location + start & finish) or Freelance. */
export function WorkModePicker({
  today,
  items,
  pos,
  online,
  current,
  onChoose,
  onCancel,
}: {
  today: string;
  items: OrderItem[];
  pos: { lat: number; lng: number } | null;
  online: boolean;
  current: WorkMode | null;
  onChoose: (w: WorkMode) => void;
  onCancel?: () => void;
}) {
  const [step, setStep] = useState<Step>('mode');
  const [startKind, setStartKind] = useState<'here' | 'office' | 'address'>(pos ? 'here' : 'office');
  const [startAddr, setStartAddr] = useState('');
  const [finishKind, setFinishKind] = useState<'anywhere' | 'start' | 'office' | 'address'>('anywhere');
  const [finishAddr, setFinishAddr] = useState('');
  const a = useAction();

  const place = async (kind: 'here' | 'office' | 'address', addr: string, what: string): Promise<Place> => {
    if (kind === 'here') {
      if (!pos) throw new Error(`Your location isn’t available yet. Pick the office or type an address for the ${what}.`);
      return { kind: 'here', label: 'where I was', lat: pos.lat, lng: pos.lng };
    }
    if (!online) throw new Error(`You’re offline. Use “Where I am now” for the ${what}, or try again when online.`);
    const r = await api.post<{ lat: number; lng: number; label: string }>('/api/field/place', kind === 'office' ? { place: 'office' } : { address: addr });
    return { kind, label: kind === 'office' ? 'the office' : r.label, lat: r.lat, lng: r.lng };
  };

  const planLocation = () =>
    a.run(async () => {
      const start = await place(startKind, startAddr, 'start');
      const finish: Place =
        finishKind === 'anywhere'
          ? { kind: 'anywhere', label: 'anywhere' }
          : finishKind === 'start'
            ? { kind: 'start', label: 'back to start', lat: start.lat, lng: start.lng }
            : await place(finishKind, finishAddr, 'finish');
      const plan = planOrder(items, { lat: start.lat!, lng: start.lng! }, finish.lat != null ? { lat: finish.lat, lng: finish.lng! } : null);
      if (!plan.stops) throw new Error('None of your papers can be attempted right now (they’d be non-qualified attempts), so there’s no route to plan. Try Freelance, or plan again when their windows open.');
      onChoose({ day: today, mode: 'route', by: 'location', start, finish, planned: plan.planned, plannedMiles: plan.miles, plannedStops: plan.stops });
    });

  return (
    <div className="wm-picker" role="group" aria-label="How are you working today?">
      {step === 'mode' && (
        <>
          <div className="wm-q">How are you working today?</div>
          <div className="wm-choices">
            <button className="wm-choice" onClick={() => setStep('by')}>
              <strong>Route</strong>
              <span>A planned order for your papers</span>
            </button>
            <button className="wm-choice" onClick={() => onChoose({ day: today, mode: 'freelance', lock: false })}>
              <strong>Freelance</strong>
              <span>Just ride. Papers sort by how close they are</span>
            </button>
          </div>
        </>
      )}
      {step === 'by' && (
        <>
          <div className="wm-q">Route based on…</div>
          <div className="wm-choices">
            <button className="wm-choice" onClick={() => onChoose({ day: today, mode: 'route', by: 'likelihood' })}>
              <strong>Likelihood of service</strong>
              <span>Best chance of catching them right now first</span>
            </button>
            <button className="wm-choice" onClick={() => setStep('where')}>
              <strong>Location</strong>
              <span>Shortest drive from where you start to where you finish</span>
            </button>
          </div>
          <button className="btn sm ghost" onClick={() => setStep('mode')}>
            Back
          </button>
        </>
      )}
      {step === 'where' && (
        <form
          className="stack"
          onSubmit={(e) => {
            e.preventDefault();
            planLocation();
          }}
        >
          <div className="wm-q">Where do you start and finish?</div>
          <div className="wm-where">
            <fieldset>
              <legend>Start</legend>
              <label>
                <input type="radio" name="wm-start" checked={startKind === 'here'} onChange={() => setStartKind('here')} /> Where I am now
              </label>
              <label>
                <input type="radio" name="wm-start" checked={startKind === 'office'} onChange={() => setStartKind('office')} /> The office
              </label>
              <label>
                <input type="radio" name="wm-start" checked={startKind === 'address'} onChange={() => setStartKind('address')} /> An address
              </label>
              {startKind === 'address' && <input aria-label="Start address" value={startAddr} onChange={(e) => setStartAddr(e.target.value)} placeholder="123 Main St, Chester" required />}
            </fieldset>
            <fieldset>
              <legend>Finish</legend>
              <label>
                <input type="radio" name="wm-finish" checked={finishKind === 'anywhere'} onChange={() => setFinishKind('anywhere')} /> Anywhere (end at the last paper)
              </label>
              <label>
                <input type="radio" name="wm-finish" checked={finishKind === 'start'} onChange={() => setFinishKind('start')} /> Back where I started
              </label>
              <label>
                <input type="radio" name="wm-finish" checked={finishKind === 'office'} onChange={() => setFinishKind('office')} /> The office
              </label>
              <label>
                <input type="radio" name="wm-finish" checked={finishKind === 'address'} onChange={() => setFinishKind('address')} /> An address
              </label>
              {finishKind === 'address' && <input aria-label="Finish address" value={finishAddr} onChange={(e) => setFinishAddr(e.target.value)} placeholder="Home, substation…" required />}
            </fieldset>
          </div>
          <div className="row gap">
            <button className="btn primary" disabled={a.busy}>
              {a.busy ? 'Planning…' : 'Plan my route'}
            </button>
            <button type="button" className="btn ghost" onClick={() => setStep('by')}>
              Back
            </button>
          </div>
        </form>
      )}
      {current && onCancel && step === 'mode' && (
        <button className="btn sm ghost" onClick={onCancel}>
          Keep “{describeMode(current)}”
        </button>
      )}
      <ErrorBox error={a.error} />
    </div>
  );
}

/** The current mode, with Change, Re-plan (route by location) and the freelance lock. */
export function WorkModeBar({
  wm,
  items,
  pos,
  order,
  onChange,
  onPick,
}: {
  wm: WorkMode;
  items: OrderItem[];
  pos: { lat: number; lng: number } | null;
  order: string[] | null;
  onChange: (w: WorkMode) => void;
  onPick: () => void;
}) {
  const replan = () => {
    if (!pos) return;
    const fin = wm.finish?.kind === 'start' ? { lat: wm.start!.lat!, lng: wm.start!.lng! } : wm.finish?.lat != null ? { lat: wm.finish.lat, lng: wm.finish.lng! } : null;
    const plan = planOrder(items, pos, fin);
    onChange({ ...wm, start: { kind: 'here', label: 'where I was', lat: pos.lat, lng: pos.lng }, planned: plan.planned, plannedMiles: plan.miles, plannedStops: plan.stops });
  };
  // Papers whose window opened since the route was planned (or that were added): offer to fit them in.
  const newlyEligible = wm.mode === 'route' && wm.by === 'location' ? items.filter((x) => x.open && x.eligibleNow && x.pin && !(wm.planned ?? []).includes(x.id)).length : 0;
  return (
    <div className="wm-bar">
      <span className="wm-now">
        <span className="wm-k">MODE</span> {describeMode(wm)}
      </span>
      {wm.mode === 'freelance' && (
        <label className="wm-lock">
          <input type="checkbox" checked={!!wm.lock} onChange={(e) => onChange({ ...wm, lock: e.target.checked, frozen: e.target.checked ? order ?? [] : undefined })} /> Don’t rearrange by location
        </label>
      )}
      {wm.mode === 'route' && wm.by === 'location' && (
        <button className={`btn sm${newlyEligible ? ' primary' : ''}`} onClick={replan} disabled={!pos} title={pos ? 'Plan again from where you are now' : 'Waiting for your location'}>
          {newlyEligible ? `${newlyEligible} more eligible now — re-plan from here` : 'Re-plan from here'}
        </button>
      )}
      <button className="btn sm ghost" onClick={onPick}>
        Change mode
      </button>
    </div>
  );
}
